import {
  Autocomplete,
  MultiSelect,
  NumberInput,
  Select,
  TextInput,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { useTranslation } from 'react-i18next';
import { DatePickerInput } from 'shared/ui/Inputs/DatePickerInput/DatePickerInput';
import type { DatePickerInputValue } from '../../../Inputs/DatePickerInput/DatePickerInput';
import type { FilterType } from './types';

type BaseFilterProps = {
  type?: FilterType;
  value?: any;
  placeholder?: string;
  data?: { value: string; label: string }[];
  min?: number;
  max?: number;
  step?: number;
  minDate?: Date;
  maxDate?: Date;
  Component?: React.ComponentType<any>;
  filterProps?: Record<string, any>;
};

type CustomFilterProps = {
  type: undefined;
  Component: React.ComponentType<any>;
  filterProps?: Record<string, any>;
};

type TextFilterProps = BaseFilterProps & {
  type: 'text';
  value: string;
  onChange: (value: string) => void;
};

type NumberFilterProps = BaseFilterProps & {
  type: 'number';
  value: number | undefined;
  onChange: (value: number | undefined) => void;
};

type SelectFilterProps = BaseFilterProps & {
  type: 'select';
  value: string | null;
  onChange: (value: string | null) => void;
};

type MultiSelectFilterProps = BaseFilterProps & {
  type: 'multiselect';
  value: string[] | undefined;
  onChange: (value: string[] | undefined) => void;
};

type DateFilterProps = BaseFilterProps & {
  type: 'date';
  value: Date | null;
  onChange: (value: Date | null) => void;
};

type DateRangeFilterProps = BaseFilterProps & {
  type: 'date-range';
  value: DatePickerInputValue;
  onChange: (value: DatePickerInputValue) => void;
};

type AutocompleteFilterProps = BaseFilterProps & {
  type: 'autocomplete';
  value: string;
  onChange: (value: string) => void;
};

export type FilterProps =
  | AutocompleteFilterProps
  | CustomFilterProps
  | TextFilterProps
  | NumberFilterProps
  | SelectFilterProps
  | MultiSelectFilterProps
  | DateFilterProps
  | DateRangeFilterProps;

const commonStyles = { style: { width: '100%' } };

export const Filter: React.FC<FilterProps> = ({
  type,
  Component,
  filterProps,
  ...props
}) => {
  const { t } = useTranslation();
  const {
    placeholder,
    min,
    max,
    step = 1,
    minDate,
    maxDate,
  } = props as BaseFilterProps;

  if (Component) {
    // If a custom component is provided, render it with the props
    return <Component {...props} placeholder={placeholder} {...commonStyles} />;
  }

  switch (type) {
    case 'autocomplete': {
      const { value, onChange } = props as AutocompleteFilterProps;
      return (
        <Autocomplete
          data={[]}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          {...commonStyles}
          {...filterProps}
        />
      );
    }

    case 'text': {
      const { value, onChange } = props as TextFilterProps;
      return (
        <TextInput
          value={value}
          onChange={e => onChange(e.currentTarget.value)}
          placeholder={placeholder}
          {...commonStyles}
          {...filterProps}
        />
      );
    }

    case 'number': {
      const { value, onChange } = props as NumberFilterProps;
      return (
        <NumberInput
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          min={min}
          max={max}
          step={step}
          {...commonStyles}
          {...filterProps}
        />
      );
    }

    case 'select': {
      const { value, onChange, data = [] } = props as SelectFilterProps;
      return (
        <Select
          rightSection={null}
          withinPortal
          value={value}
          onChange={onChange}
          data={data.map(d => ({ ...d, label: t(d.label) }))}
          placeholder={placeholder}
          searchable
          clearable
          {...commonStyles}
          {...filterProps}
        />
      );
    }

    case 'multiselect': {
      const { value, onChange, data = [] } = props as MultiSelectFilterProps;
      return (
        <MultiSelect
          withinPortal
          value={value}
          onChange={onChange}
          data={data}
          placeholder={placeholder}
          searchable
          clearable
          {...commonStyles}
          {...filterProps}
        />
      );
    }

    case 'date': {
      const { value, onChange } = props as DateFilterProps;
      return (
        <DatePicker
          withinPortal
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          minDate={minDate}
          maxDate={maxDate}
          {...commonStyles}
          {...filterProps}
        />
      );
    }

    case 'date-range': {
      const { value, onChange } = props as DateRangeFilterProps;
      return (
        <DatePickerInput
          errorProps={{
            style: {
              display: 'none',
            },
          }}
          date={value || [null, null]}
          setDate={onChange as any}
          {...commonStyles}
          {...filterProps}
        />
      );
    }
  }

  // eslint-disable-next-line no-console
  console.warn(`Filter type is not recognized, given: ${type}`);

  return null;
};
